import * as React from 'react';
import { CircularProgress, makeStyles, Tooltip } from '@material-ui/core';
import StackedArea, { StackedAreaDataKey, StackedAreaDatum } from '../charts/stacked-area';
import { Error } from '@material-ui/icons';
import { status } from '../theme/colors';
import TextStyles from '../theme/text';
import { Trends } from './models';

interface TrendsVizProps {
  parentCollapsed: boolean;
  trends: Trends;
  accessToken?: any;
}

const useStyles = makeStyles({
  errorIcon: {
    color: status.error.dark,
    opacity: '0.7',
    '&:hover': {
      opacity: 1
    }
  },
  noData: {
    ...TextStyles.label1
  }
});

/**
 * Render the trends graph at the end of a row of the table, using either data
 * included in the datagrid data or lazy loading data for each row when the
 * parent row is opened
 */
const DataGridTrendsViz = ({ trends, parentCollapsed, accessToken }: TrendsVizProps) => {
  const classes = useStyles();
  const [loading, setLoading] = React.useState<boolean>('apiPath' in trends ? true : false);
  const [data, setData] = React.useState<StackedAreaDatum[]>('data' in trends && trends.data);
  const [key, setKey] = React.useState<StackedAreaDataKey>('dataKey' in trends && trends.dataKey);
  const [loadError, setLoadError] = React.useState<string>('');

  React.useEffect(() => {
    if (loading && !parentCollapsed && 'apiPath' in trends && accessToken) {
      fetch(trends.apiPath, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })
        .then((res) => res.json())
        .then((resBody) => {
          if (resBody.success) {
            setData(resBody.data);
            setKey(resBody.dataKey);
            setLoading(false);
          } else {
            setLoading(false);
            setLoadError(resBody.message);
          }
        })
        .catch((err) => {
          setLoadError(String(err));
          setLoading(false);
        });
    }
  }, [loading, parentCollapsed]);

  const render = () => {
    if (loading) {
      return <CircularProgress size={20} />;
    } else if (loadError) {
      return (
        <Tooltip title={loadError}>
          <Error fontSize="small" className={classes.errorIcon} />
        </Tooltip>
      );
    } else if (data && key && data.length > 0) {
      return (
        <StackedArea
          data={data}
          dataKey={key}
          viewBoxWidth={300}
          viewBoxHeight={50}
          svgHeight={50}
        />
      );
    } else {
      return (
        <div className={classes.noData}>
          <p>(No data available.)</p>
        </div>
      );
    }
  };

  return <div>{render()}</div>;
};

export default DataGridTrendsViz;
